import React, { useState, useRef, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import axiosConfig from "../../Services/axiosConfig";
import { setWishlistItems } from "../../redux/wishlistSlice";
import CountryCodeSelect from "../../../../Utils/CountryCodeSelect";
import { getCountryPhoneConfig, validateCountryPhoneNumber } from "../../../../Utils/countries";
import "./login.css"

const LoginModal = ({ show, onHide, onLoginSuccess }) => {
  const dispatch = useDispatch();
  let inpRef = useRef(null);
  const [step, setStep] = useState("input");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [contact, setContact] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [contactType, setContactType] = useState("");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [agree, setAgree] = useState(false);
  const [clientName, setClientName] = useState("RentExcel");

  const phoneConfig = getCountryPhoneConfig(countryCode);

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const res = await axiosConfig.get("/sitedata/site-config/");
        if (res?.data?.tenant?.company_name) {
          setClientName(res.data.tenant.company_name);
        }
      } catch (err) {
        console.warn("Failed to fetch site config for client name", err);
      }
    };
    fetchSiteData();
  }, []);

  const refs = useRef([...Array(6)].map(() => React.createRef()));

  const reset = () => {
    setStep("input");
    setContact("");
    setCountryCode("+1");
    setContactType("");
    setDigits(["", "", "", "", "", ""]);
    setMsg("");
    setErr("");
    setLoading(false);
    setSecondsLeft(0);
    setAgree(false);
  };
  useEffect(() => {
    if (inpRef.current) {
      inpRef.current.focus()
    }
  }, [])
  const close = () => {
    reset();
    onHide();
  };

  useEffect(() => {
    if (step === "otp") setSecondsLeft(30);
  }, [step]);



  useEffect(() => {
    if (step !== "otp" || secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [step, secondsLeft]);

  const sendOtp = async () => {
    const trimmed = contact.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isNumericOnly = /^\d+$/.test(trimmed);

    if (!trimmed) {
      setErr("Please enter mobile number or email");
      return;
    }

    if (isNumericOnly) {
      const phoneError = validateCountryPhoneNumber(trimmed, countryCode);
      if (phoneError) {
        setErr(phoneError);
        return;
      }
      setContactType("mobile");
    }
    else {
      if (trimmed.length > 254) {
        setErr("Email exceeds maximum allowed length (254 characters)");
        return;
      }
      if (emailRegex.test(trimmed)) {
        setContactType("email");
      } else {
        setErr(trimmed.includes("@")
          ? "Enter a valid email format (e.g., user@example.com)"
          : "Enter either a valid email or mobile number");
        return;
      }
    }

    setLoading(true);
    try {
      const formattedContact = trimmed;

      const { data } = await axiosConfig.post("accounts/validate_contact/", {
        contact: formattedContact,
        role: "Customer",
        country_code: countryCode || "+1",
      });
      setMsg(data.message || `OTP sent to ${trimmed}`);
      setErr("");
      setStep("otp");
      setSecondsLeft(30);
      setDigits(["", "", "", "", "", ""]);
    } catch (e) {
      setErr(e.response?.data?.error || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };
  const handleUserDataSubmit = async () => {
    if (!userName.trim()) {
      setErr("Name is required");
      return;
    }

    if (userEmail.length > 254) {
      setErr("Email exceeds maximum allowed length (254 characters)");
      return;
    }

    if (!userEmail.trim()) {
      setErr("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      setErr("Enter a valid email");
      return;
    }

    setErr("");
    setLoading(true);

    try {
      const res = await axiosConfig.patch(
        `/user_data/users/${localStorage.getItem("userid")}/`,
        {
          full_name: userName,
          email: userEmail,
        }
      );

      // Save updated name
      localStorage.setItem("name", res?.data.results?.full_name);

      // Notify all components to re-sync auth state
      window.dispatchEvent(new Event("auth-changed"));

      // Notify parent component
      if (onLoginSuccess) onLoginSuccess(localStorage.getItem("userid"));

      // 👉 NEW USER → close modal after saving data
      close();

    } catch (e) {
      setErr(e.response?.data?.error || "Failed to save user data");
    } finally {
      setLoading(false);
    }
  };



  const verifyOtp = async (currentOtp) => {
    const otp = typeof currentOtp === "string" ? currentOtp : digits.join("");
    if (otp.length !== 6) {
      setErr("Enter full 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const trimmedContact = contact.trim();
      const formattedContact = trimmedContact;

      const { data } = await axiosConfig.post("accounts/verify_otp/", {
        contact: formattedContact,
        otp,
        country_code: countryCode || "+1",
      });

      if (data.token) localStorage.setItem("token", data.token);
      if (data.user_id) {
        localStorage.setItem("userid", data.user_id);
        // Fetch and sync wishlist to Redux immediately on login
        try {
          const res = await axiosConfig(`/catlog/wishlists/?user=${data.user_id}`);
          const reduxItems = res?.data?.results?.map((item) => ({
            id: item.varient?.id,
            type: item.type,
            wishlistId: item.id,
          })) || [];
          dispatch(setWishlistItems(reduxItems));
        } catch (err) {
          console.error("Failed to sync wishlist on login", err);
        }
      }
      if (data.name) localStorage.setItem("name", data.name);

      setMsg(data.message || "Verified!");
      setErr("");

      if (data.new_user === true) {
        setStep("userdata");
      } else {
        window.dispatchEvent(new Event("auth-changed"));
        if (onLoginSuccess) onLoginSuccess(data.user_id);
        close();
      }

    } catch (e) {
      setErr(e.response?.data?.message || "Wrong OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (v, i) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) {
      refs.current[i + 1].current.focus();
    } else if (v && i === 5) {
      verifyOtp(next.join(""));
    }
  };

  const handleKeyDown = (e, i) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs.current[i - 1].current.focus();
    }
  };

  return (
    <Modal show={show} onHide={close} centered className="template-temp1" onEntered={() => inpRef.current?.focus()}>
      <Modal.Header closeButton>
        <Modal.Title>
          {step === "input"}
          {step === "otp" && "Enter OTP"}
          {step === "success" && "Verified"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {step === "otp" && msg && (
          <div className="validation-text">
            <span>
              Enter 6-digit code sent to your {contactType === "mobile" ? "phone" : "email"}{" "}
              {contactType === "mobile" ? `${countryCode} ${contact}` : contact}
            </span>
            <button
              onClick={() => {
                setStep("input");
                setDigits(["", "", "", "", "", ""]);
                setErr("");
                setMsg("");
              }}
            >
              Change
            </button>
          </div>
        )}


        {loading && (
          <div className="text-center my-2">
            {/* <Spinner animation="border" size="xl" /> */}
            <img src={require("../Assets/spinner.gif")} height={100} width={100} />
          </div>
        )}

        {step === "input" && (
          <div className="text-center">
            <div className="heading">
              {/* <div>Let’s</div> */}
              <div> <span>Login</span></div>
            </div>
            <p className="login-title">
              Enter your mobile number or email to proceed
            </p>
            <div className="d-flex align-items-center mb-3" style={{ justifyContent: "center", gap: "8px", maxWidth: "340px", margin: "0 auto" }}>
              <CountryCodeSelect
                value={countryCode}
                onChange={(dialCode) => {
                  setCountryCode(dialCode);
                  const nextConfig = getCountryPhoneConfig(dialCode);
                  if (/^\d+$/.test(contact)) {
                    setContact((prev) => prev.slice(0, nextConfig.maxLength));
                  }
                }}
              />
              <input
                className="form-control"
                style={{ flex: 1, border: "1px solid #cbd5e1", minHeight: "44px" }}
                placeholder={phoneConfig.placeholder ? `${phoneConfig.placeholder} / Email` : "Mobile Number / Email"}
                value={contact}
                ref={inpRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (agree) sendOtp();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;

                  // allow only numbers up to country phoneConfig max length OR email
                  if (/^\d*$/.test(value)) {
                    if (value.length <= phoneConfig.maxLength) {
                      setContact(value);
                      setErr("");
                    }
                  } else {
                    // email: enforce 254 char max (RFC 5321)
                    if (value.length > 254) {
                      setErr("Email exceeds maximum allowed length (254 characters)");
                    } else {
                      setContact(value);
                      setErr("");
                    }
                  }
                }}
              />
            </div>
            {err && <div className="text-danger mb-3" style={{ fontSize: "0.875rem", textAlign: "center" }}>{err}</div>}

            <button
              className="next-btn-login"
              disabled={loading || !agree}
              onClick={sendOtp}
            >
              Next
            </button>
            <div className="mt-3" style={{ fontSize: "12px", color: "#6c757d", display: "flex", alignItems: "flex-start", gap: "10px", textAlign: "left", width: "100%" }}>
              <input type="checkbox" id="agreeCheckbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: "2px", cursor: "pointer", width: "16px", height: "16px", flexShrink: 0 }} />
              <label
                htmlFor="agreeCheckbox"
                style={{ cursor: "pointer", margin: 0, lineHeight: "1.4" }}
              >
                By checking this box, you consent to receive SMS text messages from{" "}
                {clientName} for account verification, login authentication, and
                security notifications. Message frequency varies. Msg & data rates
                may apply. Reply STOP to cancel, HELP for help. Consent is not a
                condition of purchase or use. See our{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>
                .
              </label>
            </div>
          </div>
        )}


        {step === "otp" && (
          <>
            <div className="d-flex justify-content-center mb-3">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={refs.current[i]}
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleChange(e.target.value, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  className="form-control text-center mx-1"
                  style={{ width: 48, fontSize: "1.5rem", border: "2px solid black" }}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            {err && <div className="text-danger mb-3" style={{ fontSize: "0.875rem", textAlign: "center" }}>{err}</div>}

            {secondsLeft > 0 ? (
              <p className="small text-muted mb-2 d-flex justify-content-center">
                Resend OTP in {secondsLeft}s
              </p>
            ) : (
              <button
                className="resendotp"
                onClick={sendOtp}
                disabled={loading}
              >
                Resend OTP
              </button>
            )}

            <button className="next-btn-login" onClick={verifyOtp}>
              Submit
            </button>
          </>
        )}
        {step === "userdata" && (
          <div className="userdata-form">

            <div className="mb-3">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                style={{ border: "1px solid" }}
                type="text"
                placeholder="Enter Name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                className="form-control"
                style={{ border: "1px solid" }}
                type="email"
                placeholder="Enter Email"
                value={userEmail}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 254) {
                    setUserEmail(value);
                    setErr("");
                  } else {
                    setErr("Email exceeds maximum allowed length (254 characters)");
                  }
                }}
              />
            </div>

            {err && (
              <div className="text-danger mb-2 small">
                {err}
              </div>
            )}

            <button
              className="savecontinue-btn"
              onClick={handleUserDataSubmit}
            >
              Save & Continue
            </button>

          </div>
        )}


        {step === "success" && (
          <div className="text-center">
            <h5 className="mb-0">Verified Successfully</h5>
            <button className="next-btn-login" onClick={close}>
              Close
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
